export type FormCache = {
  categories: any[];
  tags: any[];
  members: any[];
  paymentMethods: any[];
  savedAt: string;
};

export interface KeyValueStore {
  getItem(k: string): string | null;
  setItem(k: string, v: string): void;
  removeItem(k: string): void;
  keys(): string[];
}

export function localStorageStore(ls: Storage): KeyValueStore {
  return {
    getItem: (key) => ls.getItem(key),
    setItem: (key, value) => ls.setItem(key, value),
    removeItem: (key) => ls.removeItem(key),
    keys: () => {
      const keys: string[] = [];
      for (let i = 0; i < ls.length; i++) {
        const key = ls.key(i);
        if (key !== null) keys.push(key);
      }
      return keys;
    },
  };
}

export const CACHE_PREFIX = 'bk_cache_';

export function cacheKey(householdId: string): string {
  return `${CACHE_PREFIX}${householdId}_form`;
}

export function saveFormCache(
  store: KeyValueStore,
  householdId: string | null,
  partial: Partial<Omit<FormCache, 'savedAt'>>,
): boolean {
  if (!householdId) return false;
  try {
    const existing = loadFormCache(store, householdId);
    const cache: FormCache = {
      categories: [], tags: [], members: [], paymentMethods: [],
      ...existing,
      ...partial,
      savedAt: new Date().toISOString(),
    };
    store.setItem(cacheKey(householdId), JSON.stringify(cache));
    return true;
  } catch {
    return false;
  }
}

export function loadFormCache(store: KeyValueStore, householdId: string | null): FormCache | null {
  if (!householdId) return null;
  const key = cacheKey(householdId);
  const raw = store.getItem(key);
  if (raw === null) return null;
  try {
    const parsed = JSON.parse(raw);
    const ok = parsed && typeof parsed === 'object'
      && ['categories', 'tags', 'members', 'paymentMethods'].every((k) => Array.isArray(parsed[k]));
    if (!ok) throw new Error('bad shape');
    return parsed;
  } catch {
    store.removeItem(key);
    return null;
  }
}

export function clearAllFormCaches(store: KeyValueStore): number {
  const keys = store.keys().filter((key) => key.startsWith(CACHE_PREFIX));
  for (const key of keys) store.removeItem(key);
  return keys.length;
}
